import { useArticles } from "@faire/help-center-utils/hooks/article/useArticles";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button as ButtonIcon } from "@faire/web--source/slate/icons/foundation/Button";
import React from "react";

import { SelfServeNodes } from "../../types";
import { BrandReferralCreditDiscrepancyIA } from "../instantAnswers/referrals-brandReferralsCreditDiscrepancy";
import { BrandReferralEarnedCreditIA } from "../instantAnswers/referrals-brandReferralsEarnCredit";
import { BrandReferralHowDoReferralsWorkIA } from "../instantAnswers/referrals-brandReferralsHowDoReferralsWork";
import { BrandReferralHowToReferBrandIA } from "../instantAnswers/referrals-brandReferralsHowToReferBrand";
import { BrandReferralStatusIA } from "../instantAnswers/referrals-brandReferralsStatus";
import { BrandReferralUseCreditIA } from "../instantAnswers/referrals-brandReferralsUseCredit";
import { HowDoesSignupWorkIA } from "../instantAnswers/referrals-HowDoesSignupWork";
import { WhenDoesSignupExpireIA } from "../instantAnswers/referrals-whenDoesSignupExpire";

export const useReferralsRetailerNodes = (): SelfServeNodes => {
  const { strictLocalize } = useStrictLocalization();
  const articles = useArticles();
  return {
    Referrals: {
      id: "Referrals",
      label: strictLocalize({
        defaultMessage: "Referrals",
        description: {
          text: "A top level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      icon: <ButtonIcon fontSize="24px" color="faireBlack" />,
      articles: [
        articles.Referrals.CAN_I_REFER_BRANDS_TO_FAIRE,
        articles.Referrals.USE_REFERRAL_PROMOTION,
      ],
      children: ["SignUpOfferFaireDirect", "ReferBrand"],
      level: 1,
      sidebarTitle: strictLocalize({
        defaultMessage: "Referral issue",
        description: {
          text: "Title for displaying the issue on the order summary sidebar",
        },
      }),
    },
    SignUpOfferFaireDirect: {
      id: "SignUpOfferFaireDirect",
      label: strictLocalize({
        defaultMessage: "Signup offer (Faire Direct)",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "HowDoesSignupWork",
        "WhenDoesSignupExpire",
        "WhyNoSignupOffer",
      ],
      level: 2,
    },
    ReferBrand: {
      id: "ReferBrand",
      label: strictLocalize({
        defaultMessage: "Refer a brand",
        description: {
          text: "A second level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      children: [
        "BrandReferralsStatus",
        "BrandReferralsEarnCredit",
        "BrandReferralsUseCredit",
        "CreditDiscrepancy",
        "HowToReferBrand",
        "HowBrandReferralsWork",
      ],
      level: 2,
    },
    HowDoesSignupWork: {
      id: "HowDoesSignupWork",
      label: strictLocalize({
        defaultMessage: "How does the signup offer work",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Referrals.USE_REFERRAL_PROMOTION],
      instantAnswer: HowDoesSignupWorkIA(strictLocalize),
      level: 3,
    },
    WhenDoesSignupExpire: {
      id: "WhenDoesSignupExpire",
      label: strictLocalize({
        defaultMessage: "When does my signup offer expire",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: WhenDoesSignupExpireIA(strictLocalize),
      level: 3,
    },
    WhyNoSignupOffer: {
      id: "WhyNoSignupOffer",
      label: strictLocalize({
        defaultMessage: "Why didn’t I get the signup offer",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      instantAnswer: WhenDoesSignupExpireIA(strictLocalize),
      level: 3,
    },
    BrandReferralsStatus: {
      id: "BrandReferralsStatus",
      label: strictLocalize({
        defaultMessage: "How to check the status of brand referrals",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Referrals.CAN_I_REFER_BRANDS_TO_FAIRE],
      instantAnswer: BrandReferralStatusIA(strictLocalize),
      level: 3,
    },
    BrandReferralsEarnCredit: {
      id: "BrandReferralsEarnCredit",
      label: strictLocalize({
        defaultMessage: "How to earn credit from brand referrals",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Referrals.CAN_I_REFER_BRANDS_TO_FAIRE],
      instantAnswer: BrandReferralEarnedCreditIA(strictLocalize),
      level: 3,
    },
    BrandReferralsUseCredit: {
      id: "BrandReferralsUseCredit",
      label: strictLocalize({
        defaultMessage: "How to use the credit from brand referrals",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Referrals.CAN_I_REFER_BRANDS_TO_FAIRE],
      instantAnswer: BrandReferralUseCreditIA(strictLocalize),
      level: 3,
    },
    CreditDiscrepancy: {
      id: "CreditDiscrepancy",
      label: strictLocalize({
        defaultMessage: "Why is there a credit discrepancy",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Referrals.CAN_I_REFER_BRANDS_TO_FAIRE],
      instantAnswer: BrandReferralCreditDiscrepancyIA(strictLocalize),
      level: 3,
    },
    HowToReferBrand: {
      id: "HowToReferBrand",
      label: strictLocalize({
        defaultMessage: "How to refer a brand",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [articles.Referrals.CAN_I_REFER_BRANDS_TO_FAIRE],
      instantAnswer: BrandReferralHowToReferBrandIA(strictLocalize),
      level: 3,
    },
    HowBrandReferralsWork: {
      id: "HowBrandReferralsWork",
      label: strictLocalize({
        defaultMessage: "How do brand referrals work",
        description: {
          text: "A third level issue node in the full page self-serve experience of Faire's Help Center",
        },
      }),
      articles: [
        articles.Referrals.CAN_I_REFER_BRANDS_TO_FAIRE,
        articles.FAQ.BRAND_REFERRAL_PROGRAM_TERMS_CONDITIONS,
      ],
      instantAnswer: BrandReferralHowDoReferralsWorkIA(strictLocalize),
      level: 3,
    },
  };
};
